import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';

// import { EnvironmentService } from '../../../services/enviroment.service';
// import { environment } from '../../../services/global-environment';

import { environment } from '../../../services/global-environment';

interface Law {
  key: string;
  lawName: string;
  lawUrl: string;
  date: string;
  status: string;
  lawHierarchy: string;
  DepartName?: string;
  id:string;
  laws?: {
    law_id: string;
    law_source: string;
    law_url: string;
    law_key: string;
    law_name: string;
    law_publish_date: string;
    law_modified_date: string;
    law_version: string;
    law_category: string;
    law_hierarchy: string;
    law_articles: Array<{
      article_id: string;
      article_type: string;
      article_no: string;
      article_content: string;
    }>;
  };
  lawArticlesCount: number;
}

// 修改 LawCategory 接口
interface LawCategory {
  Source?: string;     // 添加 Source 作為可選屬性
  DepartName: string;
  type: string;
  laws: Law[];
}

interface YearMonthNode {
  year: number;
  months: {
    month: number;
    laws: Law[];
  }[];
}

@Component({
  selector: 'app-regulation-bd',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './regulation-bd.component.html',
  styleUrl: './regulation-bd.component.scss'
})
export class RegulationBdComponent implements OnInit {
  // 顯示模式
  displayMode: 'timeline' | 'directory' = 'directory';

  // 所有法規資料
  lawData: LawCategory[] = [];

  // 時間軸資料
  timelineData: YearMonthNode[] = [];

  // 分類資料
  departmentTypes: {
    department: string,   // 母階層
    categories: {         // 子階層分類
      name: string,       // 類型名稱
      expanded: boolean   // 是否展開
    }[],
    expanded: boolean     // 部門層級是否展開
  }[] = [];

  // 選擇的過濾器
  selectedDepartments: string[] = [];
  selectedDepartment: string = '';
  selectedCategory: string = ''; // 選擇的子階層
  selectedType: string = '';
  selectedYearMonth: { year: number, month: number } | null = null;

  // 顯示的法規列表
  displayedLaws: Law[] = [];

  // 載入狀態
  isLoading = false;

  // 新增到組件類中
  selectedParentCategory: string = '';

  // 存儲展開狀態
  private expandedCategories = new Set<string>();

  // 部門清單
  readonly initialDepartments: string[] = ['全國法規資料庫', '勞動部', '環境部'];

  // 添加搜尋相關變數
  searchKeyword: string = '';
  searchStartDate: string = '';
  searchEndDate: string = '';
  isSearchActive: boolean = false;
  originalDisplayedLaws: Law[] = []; // 儲存未過濾前的法規清單

  // 添加一個變數來存儲搜尋結果對應的分類信息
  searchResultCategories: {
    parentName: string;
    types: {
      typeName: string;
      count: number;
    }[];
  }[] = [];

  // 添加屬性來存儲當前選中的法規詳細資料
  selectedLawDetails: Law | null = null;

  // 添加通知相關屬性
  notificationMessage: string = '';
  showNotification: boolean = false;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    // 先初始化部門資料
    this.initializeDepartmentData();
  }

  // 初始化部門資料
  initializeDepartmentData(): void {
    // 為每個預設部門創建一個基本結構
    this.departmentTypes = this.initialDepartments.map(department => ({
      department,
      categories: [],
      expanded: false
    }));

    // 預設選擇第一個部門
    if (this.departmentTypes.length > 0) {
      // 如果目錄模式是預設模式，就設置選中的部門
      if (this.displayMode === 'directory') {
        this.selectedDepartment = this.departmentTypes[0].department;
      }
      // 如果是時間軸模式，選擇所有部門
      else {
        this.selectedDepartments = [...this.initialDepartments];
      }
    }
  }

  // 處理原始資料
  processData(): void {
    // 處理部門和類型
    this.processDepartmentTypes();

    // 處理時間軸資料
    this.processTimelineData();

    // 預設選擇所有部門
    this.selectedDepartments = this.departmentTypes.map(d => d.department);

    // 預設顯示最新月份的法規
    if (this.timelineData.length > 0 && this.timelineData[0].months.length > 0) {
      this.selectedYearMonth = {
        year: this.timelineData[0].year,
        month: this.timelineData[0].months[0].month
      };
      this.updateDisplayedLaws();
    }
  }

  // 處理部門和類型
  processDepartmentTypes(): void {
    // 使用 Map 來組織不同層級的資料
    const deptCategoryMap = new Map<string, Set<string>>();

    // 對每個部門數據進行處理
    for (const category of this.lawData) {
      // 取得部門名稱和類型
      const deptName = category.DepartName;
      const typeValue = category.type;

      // 確保該部門存在於映射中
      if (!deptCategoryMap.has(deptName)) {
        deptCategoryMap.set(deptName, new Set<string>());
      }

      // 添加類型到該部門的集合中
      deptCategoryMap.get(deptName)!.add(typeValue);
    }

    // 將映射轉換為組件使用的格式
    this.departmentTypes = Array.from(deptCategoryMap.entries())
      .map(([department, typeSet]) => ({
        department,
        categories: Array.from(typeSet).map(name => ({
          name,
          expanded: false  // 初始狀態為收合
        })),
        expanded: false    // 部門層級也可收合
      }));

    console.log("departmentTypes", this.departmentTypes);
  }

  // 處理時間軸資料
  processTimelineData(): void {
    // 用來臨時存儲年月數據的映射
    const yearMonthMap: Map<number, Map<number, Law[]>> = new Map();

    // 遍歷所有法規
    for (const category of this.lawData) {
      for (const law of category.laws) {
        // 解析日期
        const dateParts = law.date.split('-');
        if (dateParts.length !== 3) continue;

        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]);

        // 確保年份存在於映射中
        if (!yearMonthMap.has(year)) {
          yearMonthMap.set(year, new Map<number, Law[]>());
        }

        // 確保月份存在於年份映射中
        const monthMap = yearMonthMap.get(year)!;
        if (!monthMap.has(month)) {
          monthMap.set(month, []);
        }

        // 將法規添加到對應的年月中
        monthMap.get(month)!.push({ ...law, DepartName: category.DepartName });
      }
    }

    // 將映射轉換為陣列格式
    this.timelineData = Array.from(yearMonthMap.entries())
      .map(([year, monthMap]) => ({
        year,
        months: Array.from(monthMap.entries())
          .map(([month, laws]) => ({
            month,
            laws
          }))
          .sort((a, b) => b.month - a.month) // 月份倒序排列
      }))
      .sort((a, b) => b.year - a.year); // 年份倒序排列
  }

  // 切換顯示模式
  changeDisplayMode(mode: 'timeline' | 'directory'): void {
    this.displayMode = mode;

    // 重置選擇
    if (mode === 'timeline') {
      this.selectedDepartments = this.departmentTypes.map(d => d.department);
      this.selectedDepartment = '';
      this.selectedCategory = '';
      this.selectedType = '';
    } else {
      this.selectedDepartments = [];
      this.selectedYearMonth = null;
      // 預設選擇第一個部門
      if (this.departmentTypes.length > 0) {
        this.selectedDepartment = this.departmentTypes[0].department;
        // 預設選擇此部門的第一個類型
        if (this.departmentTypes[0].categories.length > 0) {
          this.selectedCategory = this.departmentTypes[0].categories[0].name;
        }
      }
    }

    this.updateDisplayedLaws();
  }

  // 選擇部門（目錄模式）
  selectDepartment(department: string): void {
    this.selectedDepartment = department;
    this.selectedParentCategory = ''; // 重置選中的母階層
    this.selectedType = '';  // 重置選中的子階層
    this.isLoading = true;

    // 使用API取得該部門的法規資料
    this.http.post<any>(`${environment.apiUrl}/api/law_database/getBySource`, {
      sources: [department]
    }).subscribe({
      next: (response) => {
        if (response.success && response.items && response.items.length > 0) {
          // 清除該部門現有的資料
          this.lawData = this.lawData.filter(item => item.Source !== department);

          // 將API回傳的資料分組整理
          const processedItems = this.processApiResponse(response.items, department);

          // 添加到 lawData
          this.lawData = [...this.lawData, ...processedItems];

          // 更新母階層分類列表
          this.processDepartmentTypes();
        } else {
          console.error('未取得資料或資料格式錯誤', response);
        }

        this.isLoading = false;
        // 即使沒有數據，也要更新顯示（可能顯示無資料提示）
        this.updateDisplayedLaws();
      },
      error: (error) => {
        console.error('取得部門法規資料失敗', error);
        this.isLoading = false;
        // 更新顯示（可能顯示錯誤提示）
        this.updateDisplayedLaws();
      }
    });
  }

  // 處理 API 回傳的資料，轉換為程式所需格式
  private processApiResponse(items: any[], sourceName: string): LawCategory[] {

    console.log('API 回傳的資料:', items);

    // 使用 Map 進行分組
    const categoryMap = new Map<string, Map<string, Law[]>>();

    // 分類處理
    items.forEach(item => {
      const departName = item.DepartName || '未分類';
      const typeName = item.type || '未分類';

      // 確保有父分類
      if (!categoryMap.has(departName)) {
        categoryMap.set(departName, new Map<string, Law[]>());
      }

      // 確保有子分類
      const typeMap = categoryMap.get(departName)!;
      if (!typeMap.has(typeName)) {
        typeMap.set(typeName, []);
      }

      // 添加法規
      const law: Law = {
        key: item.key,
        id:item.id,
        lawName: item.lawName,
        lawUrl: item.lawUrl,
        date: item.date,
        status: item.status || '',
        lawHierarchy: item.lawHierarchy || '',
        DepartName: departName,
        lawArticlesCount: item.lawArticlesCount || 0,
        laws: item.laws || [] // 如果有子法規，則添加
      };

      typeMap.get(typeName)!.push(law);
    });

    // 轉換為 LawCategory 格式
    const result: LawCategory[] = [];
    categoryMap.forEach((typeMap, departName) => {
      typeMap.forEach((laws, typeName) => {
        result.push({
          Source: sourceName,
          DepartName: departName,
          type: typeName,
          laws: laws
        });
      });
    });

    return result;
  }

  // 選擇類型（目錄模式）
  selectType(type: string): void {
    this.selectedType = type;
    this.updateDisplayedLaws();
  }

  // 選擇特定母階層下的子類型
  selectTypeWithParent(parentName: string, type: string): void {
    // 如果有搜尋狀態，先清除
    if (this.isSearchActive) {
      this.clearSearch();
    }

    this.selectedParentCategory = parentName;
    this.selectedType = type;
    this.updateDisplayedLaws();
  }

  // 時間軸模式下選擇部門
  toggleDepartmentSelection(department: string): void {
    const index = this.selectedDepartments.indexOf(department);
    if (index > -1) {
      this.selectedDepartments.splice(index, 1);
    } else {
      this.selectedDepartments.push(department);
    }

    this.updateDisplayedLaws();
  }

  // 選擇年月（時間軸模式）
  selectYearMonth(year: number, month: number): void {
    this.selectedYearMonth = { year, month };
    this.updateDisplayedLaws();
  }

  // 更新顯示的法規列表
  updateDisplayedLaws(): void {
    if (this.displayMode === 'directory') {
      this.updateDisplayedLawsForDirectory();
    } else {
      // 時間軸模式的現有代碼
      // ...
    }
  }

  // 更新顯示的法規列表（目錄模式）
  private updateDisplayedLawsForDirectory(): void {
    if (!this.selectedDepartment || !this.selectedParentCategory || !this.selectedType) {
      this.displayedLaws = [];
      return;
    }

    // 查找匹配所選母階層和子類型的法規，加入來源條件進行過濾
    const matchingCategories = this.lawData.filter(
      c => c.Source === this.selectedDepartment &&
           c.DepartName === this.selectedParentCategory &&
           c.type === this.selectedType
    );

    if (matchingCategories.length > 0) {
      // 合併所有匹配類別的法規
      this.displayedLaws = matchingCategories.flatMap(c => c.laws || []);
    } else {
      this.displayedLaws = [];
    }
  }

  // 格式化月份顯示
  formatMonth(month: number): string {
    return month < 10 ? `0${month}` : `${month}`;
  }

  // 檢查部門是否被選中
  isDepartmentSelected(department: string): boolean {
    return this.selectedDepartments.includes(department);
  }

  // 獲取特定年月的法規數量
  getLawCount(year: number, month: number): number {
    // 只計算選定部門的法規
    const yearNode = this.timelineData.find(y => y.year === year);
    if (!yearNode) return 0;

    const monthNode = yearNode.months.find(m => m.month === month);
    if (!monthNode) return 0;

    return monthNode.laws.filter(
      law => this.selectedDepartments.includes((law as any).DepartName)
    ).length;
  }

  // 處理法規點擊，打開URL
  openLawUrl(url: string): void {
    window.open(url, '_blank');
  }

  // 打開法規詳細資訊模態框
  openLawDetailsModal(law: Law): void {
    this.selectedLawDetails = law;
    // 防止頁面滾動
    document.body.style.overflow = 'hidden';
  }

  // 關閉法規詳細資訊模態框
  closeLawDetailsModal(): void {
    this.selectedLawDetails = null;
    // 恢復頁面滾動
    document.body.style.overflow = 'auto';
  }

  // 格式化條文內容，增加自動斷行
  formatArticleContent(text: string): string {
    if (!text) return '';

    // 檢查文本是否已經包含換行符
    if (text.includes('\n')) {
      // 文本中已經有換行符，將其轉換為 HTML 換行標籤
      return text.replace(/\n/g, '<br>');
    } else {
      // 原文沒有換行符，添加格式化處理
      // 在「：」或「。」後添加 HTML 換行標籤
      const formattedText = text.replace(/([：。])/g, '$1<br>');
      return formattedText;
    }
  }

  // 修改方法：點擊檢視按鈕時呼叫 API 獲取法規詳細資訊
  fetchLawDetails(law: Law): void {
    this.isLoading = true; // 顯示加載指示器

    // 構造 API URL
    const url = `${environment.apiUrl}/api/law_database/getLawByID`;

    // 設置請求參數
    const params = new HttpParams().set('id', law.id);

    // 發送 API 請求
    this.http.get<any>(url, { params }).subscribe({
      next: (response) => {
        if (response && response.success) {
          // 複製原始法規資訊
          const lawWithDetails = { ...law };

          // 將 API 返回的 laws 數據添加到對象中
          lawWithDetails.laws = response.data;

          // 打開詳細資訊模態框
          this.openLawDetailsModal(lawWithDetails);
        } else {
          console.error('獲取法規詳細資訊失敗:', response);
          this.notificationMessage = '無法讀取法規詳細資訊';
          this.showNotification = true;
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('獲取法規詳細資訊時出現錯誤:', error);
        this.notificationMessage = '讀取法規詳細資訊時出現錯誤';
        this.showNotification = true;
        this.isLoading = false;
      }
    });
  }

  // 為所選部門獲取類型列表
  getTypesForSelectedDepartment(): string[] {
    if (!this.selectedDepartment) return [];

    const deptInfo = this.departmentTypes.find(d => d.department === this.selectedDepartment);
    return deptInfo ? deptInfo.categories.map(c => c.name) : [];
  }

  // 檢查法規列表中是否有任何法規具有狀態
  hasAnyLawWithStatus(): boolean {
    return this.displayedLaws.some(law => !!law.status);
  }

  // 檢查特定法規是否有顯示部門名稱
  shouldShowDepartmentColumn(): boolean {
    return this.displayMode === 'timeline';
  }

  // 獲取法規的部門名稱（處理可能的未定義情況）
  getDepartmentName(law: Law): string {
    return (law as any).DepartName || '';
  }

  // 展開/收合部門
  toggleDepartmentExpand(dept: any): void {
    dept.expanded = !dept.expanded;

    // 如果收合部門，且當前選中的就是該部門下的類型，則重置選擇
    if (!dept.expanded && this.selectedDepartment === dept.department) {
      this.selectedCategory = '';
      this.displayedLaws = [];
    }
  }

  // 選擇部門及類型
  selectDepartmentAndCategory(department: string, category: string): void {
    this.selectedDepartment = department;
    this.selectedCategory = category;
    this.isLoading = true;

    // 使用API取得該部門和類型的資料
    this.http.post<any>(`${environment.apiUrl}/api/law_database/getBySource`, {
      sources: [department]
    }).subscribe({
      next: (response) => {
        if (response.success && response.items && response.items.length > 0) {
          // 找出對應類型的資料
          const laws = response.items
            .filter((item: any) => item.DepartName === department && item.type === category)
            .map((item: any) => ({
              key: item.key,
              lawName: item.lawName,
              lawUrl: item.lawUrl,
              date: item.date,
              status: item.status || '',
              lawHierarchy: item.lawHierarchy || '',
              DepartName: item.DepartName
            }));

          this.displayedLaws = laws;
        } else {
          this.displayedLaws = [];
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('取得資料失敗', error);
        this.displayedLaws = [];
        this.isLoading = false;
      }
    });
  }

  // 獲取選定部門的母階層分類列表
  getDepartmentCategoriesForSelected(): { name: string, expanded: boolean, types: string[] }[] {
    if (!this.selectedDepartment) return [];

    // 搜尋模式：顯示符合搜尋條件的分類
    if (this.isSearchActive && this.searchResultCategories.length > 0) {
      return this.searchResultCategories.map(category => ({
        name: category.parentName,
        expanded: this.expandedCategories.has(category.parentName),
        types: category.types.map(t => t.typeName)
      }));
    }

    // 正常模式：顯示所有分類
    const parentCategoryMap = new Map<string, Set<string>>();

    this.lawData.forEach(item => {
      if (item.Source === this.selectedDepartment) {
        const parentName = item.DepartName;
        const typeName = item.type;

        if (!parentCategoryMap.has(parentName)) {
          parentCategoryMap.set(parentName, new Set<string>());
        }

        if (typeName) {
          parentCategoryMap.get(parentName)!.add(typeName);
        }
      }
    });

    return Array.from(parentCategoryMap.entries())
      .map(([name, typeSet]) => ({
        name,
        expanded: this.expandedCategories.has(name),
        types: Array.from(typeSet).sort()
      }));
  }

  // 展開/收合母階層分類
  toggleParentCategory(parent: { name: string, expanded: boolean, types: string[] }): void {
    // 更新展開狀態
    if (parent.expanded) {
      this.expandedCategories.delete(parent.name);
    } else {
      this.expandedCategories.add(parent.name);
    }

    // 更新本地狀態
    parent.expanded = !parent.expanded;

    console.log('Toggled:', parent.name, 'now expanded:', parent.expanded);
    console.log('Expanded categories:', Array.from(this.expandedCategories));
  }

  // 獲取某個母階層分類下的法規總數
  getParentCategoryLawCount(parentName: string): number {
    // 搜尋模式：顯示符合搜尋條件的法規數量
    if (this.isSearchActive) {
      const category = this.searchResultCategories.find(c => c.parentName === parentName);
      if (category) {
        return category.types.reduce((sum, type) => sum + type.count, 0);
      }
      return 0;
    }

    // 正常模式：顯示所有法規數量
    return this.lawData
      .filter(category => category.DepartName === parentName)
      .reduce((total, category) => {
        return total + (category.laws?.length || 0);
      }, 0);
  }

  // 獲取某個母階層分類下特定子階層的法規數量
  getTypeLawCount(parentName: string, typeName: string): number {
    // 搜尋模式：顯示符合搜尋條件的法規數量
    if (this.isSearchActive) {
      const category = this.searchResultCategories.find(c => c.parentName === parentName);
      if (category) {
        const typeInfo = category.types.find(t => t.typeName === typeName);
        return typeInfo ? typeInfo.count : 0;
      }
      return 0;
    }

    // 正常模式：顯示所有法規數量
    const categories = this.lawData.filter(
      c => c.DepartName === parentName && c.type === typeName
    );

    let count = 0;
    categories.forEach(category => {
      count += category.laws?.length || 0;
    });

    return count;
  }

  // 改進的搜尋方法
  applySearch(): void {
    // 當任何搜尋條件存在時，標記搜尋狀態為啟用
    this.isSearchActive = !!(this.searchKeyword || this.searchStartDate || this.searchEndDate);

    // 如果沒有任何搜尋條件，則清除搜尋狀態
    if (!this.isSearchActive) {
      this.clearSearch();
      return;
    }

    // 準備搜尋的資料來源
    let lawsToFilter: Law[] = [];
    let allLawsWithCategories: { law: Law, parentCategory: string, type: string }[] = [];

    if (this.selectedDepartment) {
      // 從當前部門的所有法規中搜尋，同時保留分類信息
      console.log('從所有法規資料中搜尋並記錄分類信息...');

      this.lawData
        .filter(category => category.Source === this.selectedDepartment)
        .forEach(category => {
          if (category.laws && category.laws.length > 0) {
            // 記錄每個法規所屬的母階層和類型
            category.laws.forEach(law => {
              allLawsWithCategories.push({
                law,
                parentCategory: category.DepartName,
                type: category.type
              });
              lawsToFilter.push(law);
            });
          }
        });

      // 儲存原始資料（僅包含法規，不含分類信息）
      if (this.originalDisplayedLaws.length === 0) {
        this.originalDisplayedLaws = [...lawsToFilter];
      }
    }

    console.log(`搜尋範圍: ${lawsToFilter.length} 筆資料`);

    // 如果沒有資料可搜尋，顯示訊息並返回
    if (lawsToFilter.length === 0) {
      console.log('沒有資料可供搜尋');
      this.displayedLaws = [];
      this.searchResultCategories = [];
      return;
    }

    // 應用搜尋條件
    // 建立一個映射來追蹤符合條件的法規及其分類
    const filteredLawsWithCategories = allLawsWithCategories.filter(item => {
      const law = item.law;
      let matchesKeyword = true;
      let matchesStartDate = true;
      let matchesEndDate = true;

      // 關鍵字過濾
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase();
        matchesKeyword = law.lawName.toLowerCase().includes(keyword)
      }

      // 開始日期過濾
      if (this.searchStartDate) {
        const startDate = new Date(this.searchStartDate);
        const lawDate = new Date(law.date);
        matchesStartDate = lawDate >= startDate;
      }

      // 結束日期過濾
      if (this.searchEndDate) {
        const endDate = new Date(this.searchEndDate);
        // 設置為當天結束時間
        endDate.setHours(23, 59, 59, 999);
        const lawDate = new Date(law.date);
        matchesEndDate = lawDate <= endDate;
      }

      return matchesKeyword && matchesStartDate && matchesEndDate;
    });

    // 從過濾後的結果中提取法規
    const filteredLaws = filteredLawsWithCategories.map(item => item.law);

    // 計算每個分類中符合搜尋條件的法規數量
    const categoryCountMap = new Map<string, Map<string, number>>();

    filteredLawsWithCategories.forEach(item => {
      const { parentCategory, type } = item;

      if (!categoryCountMap.has(parentCategory)) {
        categoryCountMap.set(parentCategory, new Map<string, number>());
      }

      const typeMap = categoryCountMap.get(parentCategory)!;
      typeMap.set(type, (typeMap.get(type) || 0) + 1);
    });

    // 轉換為搜尋結果分類格式
    this.searchResultCategories = Array.from(categoryCountMap.entries())
      .map(([parentName, typeMap]) => ({
        parentName,
        types: Array.from(typeMap.entries())
          .map(([typeName, count]) => ({
            typeName,
            count
          }))
          .sort((a, b) => a.typeName.localeCompare(b.typeName)) // 按類型名稱排序
      }))
      .sort((a, b) => a.parentName.localeCompare(b.parentName)); // 按母階層名稱排序

    console.log('搜尋結果分類統計:', this.searchResultCategories);

    // 更新顯示的法規列表
    this.displayedLaws = filteredLaws;

    console.log('搜尋條件已套用:', {
      keyword: this.searchKeyword,
      startDate: this.searchStartDate,
      endDate: this.searchEndDate,
      resultCount: this.displayedLaws.length
    });
  }

  // 修改清除搜尋函數
  clearSearch(): void {
    this.searchKeyword = '';
    this.searchStartDate = '';
    this.searchEndDate = '';
    this.isSearchActive = false;

    // 清空搜尋結果分類信息
    this.searchResultCategories = [];

    // 還原原始資料
    if (this.originalDisplayedLaws.length > 0) {
      // 如果有選中具體分類，恢復到該分類下的法規
      if (this.selectedParentCategory && this.selectedType) {
        this.displayedLaws = [...this.originalDisplayedLaws];
      } else {
        // 如果沒有選中具體分類，則清空顯示
        this.displayedLaws = [];
      }
      this.originalDisplayedLaws = [];
    }
  }

  // 設置日期範圍為本月
  setDateRangeForCurrentMonth(): void {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    this.searchStartDate = this.formatDateForInput(firstDayOfMonth);
    this.searchEndDate = this.formatDateForInput(today);
  }

  // 設置日期範圍為上個月
  setDateRangeForLastMonth(): void {
    const today = new Date();
    const firstDayOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

    this.searchStartDate = this.formatDateForInput(firstDayOfLastMonth);
    this.searchEndDate = this.formatDateForInput(lastDayOfLastMonth);
  }

  // 設置日期範圍為近三個月
  setDateRangeForLastThreeMonths(): void {
    const today = new Date();
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    this.searchStartDate = this.formatDateForInput(threeMonthsAgo);
    this.searchEndDate = this.formatDateForInput(today);
  }

  // 將日期格式化為 input[type="date"] 使用的格式 (YYYY-MM-DD)
  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
